import { List, useListRef } from "react-window";
import { useTheme, styled } from "@mui/material/styles";
import {
  Box,
  Button,
  useMediaQuery,
  IconButton,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React from "react";
import DoneIcon from "@mui/icons-material/Done";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import CircularProgress from "@mui/material/CircularProgress";

const LISTBOX_PADDING = 8; // px

export function RowComponent({
  index,
  itemData,
  style,
  pendingValue,
  setPendingValue,
  callBack,
}) {
  const dataSet = itemData[index];
  const inlineStyle = {
    ...style,
    top: (style.top ?? 0) + LISTBOX_PADDING,
  };

  if ("group" in dataSet) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  const { key, ...optionProps } = dataSet[0];
  const option = dataSet[3];
  const currentItem = dataSet[4];

  const isPending = pendingValue?.some((v) => v.id === option);
  const isSelectionNew = pendingValue?.some(
    (v) => v.id === option && v.isSelectionNew,
  );

  const handleAdd = (option) => {
    setPendingValue((prev) => {
      const exists = prev.some((v) => v.id === option);

      if (exists) {
        return prev.filter((v) => v.id !== option);
      } else {
        callBack({ item: currentItem, action: "add" });
        return [...prev, { id: option, isSelectionNew: true }];
      }
    });
  };

  const handleRemove = (option) => {
    if (!isSelectionNew) return;

    callBack({ item: currentItem, action: "delete" });
    setPendingValue(pendingValue.filter((v) => v.id !== option));
  };

  return (
    <li key={key} {...optionProps} style={inlineStyle}>
      <Box
        component={DoneIcon}
        sx={{ width: 17, height: 17, mr: "5px", ml: "-2px" }}
        style={{
          visibility: isPending ? "visible" : "hidden",
        }}
      />

      <Box
        sx={(t) => ({
          whiteSpace: "normal",
          wordBreak: "break-word",
          flexGrow: 1,
          "& span": {
            color: "#8b949e",
            ...t.applyStyles("light", {
              color: "#586069",
            }),
          },
        })}
      >
        {dataSet[1]}
        {/*<br />
          <span>{option.displayName}</span>*/}
      </Box>
      <IconButton
        disabled={!isSelectionNew && isPending}
        onClick={(e) => {
          e.stopPropagation(); // prevent row click
          if (isPending) handleRemove(option);
          else handleAdd(option);
        }}
        sx={{
          opacity: 0.6,
          width: 24,
          height: 24,
        }}
      >
        <span>
          {isPending ? (
            <DeleteForeverRoundedIcon
              color={!isSelectionNew ? "gray" : "warning"}
            />
          ) : (
            <AddIcon
              color={!isSelectionNew && isPending ? "gray" : "warning"}
            />
          )}
        </span>
      </IconButton>
    </li>
  );
}

// Adapter for react-window v2
RowComponent.propTypes = {
  index: PropTypes.number.isRequired,
  itemData: PropTypes.array.isRequired,
  style: PropTypes.object.isRequired,
};

export const ListboxComponent = React.forwardRef(
  function ListboxComponent(props, ref) {
    const {
      children,
      internalListRef,
      onItemsBuilt,
      pendingValue,
      setPendingValue,
      callBack,
      searching,
      ...other
    } = props;
    const itemData = [];
    const optionIndexMap = React.useMemo(() => new Map(), []);

    children.forEach((item) => {
      itemData.push(item);
      if ("children" in item && Array.isArray(item.children)) {
        itemData.push(...item.children);
      }
    });

    // Map option values to their indices in the flattened array
    itemData.forEach((item, index) => {
      if (Array.isArray(item) && item[1]) {
        optionIndexMap.set(item[1], index);
      }
    });

    React.useEffect(() => {
      if (onItemsBuilt) {
        onItemsBuilt(optionIndexMap);
      }
    }, [onItemsBuilt, optionIndexMap]);

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up("sm"), {
      noSsr: true,
    });
    const itemCount = itemData.length;
    const itemSize = smUp ? 49 : 60;

    const getChildSize = (child) => {
      if (child.hasOwnProperty("group")) {
        return 48;
      }
      return itemSize;
    };

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    // Separate className for List, other props for wrapper div (ARIA, handlers)
    const { className, style, ...otherProps } = other;
    const [showLoader, setShowLoader] = React.useState(false);
    const loaderTimeoutRef = React.useRef();
    React.useEffect(() => {
      if (searching) {
        // delay showing loader (e.g. 300ms)
        loaderTimeoutRef.current = setTimeout(() => {
          setShowLoader(true);
        }, 1000);
      } else {
        // stop loader immediately
        clearTimeout(loaderTimeoutRef.current);
        setShowLoader(false);
      }

      return () => clearTimeout(loaderTimeoutRef.current);
    }, [searching]);

    return (
      <div
        ref={ref}
        {...otherProps}
        style={{ position: "relative", width: "100%" }}
      >
        <List
          className={className}
          listRef={internalListRef}
          key={itemCount}
          rowCount={itemCount}
          rowHeight={(index) => getChildSize(itemData[index])}
          rowComponent={RowComponent}
          //rowProps={{ itemData }}
          rowProps={{
            itemData,
            pendingValue,
            setPendingValue,
            callBack,
          }}
          style={{
            height: getHeight() + 2 * LISTBOX_PADDING,
            width: "100%",
          }}
          overscanCount={5}
          tagName="ul"
        />
        {/* Loader Overlay */}
        {showLoader && (
          <Box
            sx={{
              position: "absolute",
              bottom: 4,
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              opacity: showLoader ? 1 : 0,
              transition: "opacity 0.2s ease",
            }}
          >
            <CircularProgress size={16} />
          </Box>
        )}
      </div>
    );
  },
);

ListboxComponent.propTypes = {
  children: PropTypes.any,
  className: PropTypes.string,
  internalListRef: PropTypes.any,
  onItemsBuilt: PropTypes.func.isRequired,
  style: PropTypes.object,
};

export function useVirtualize({ optionIndexMapRef }) {
  const internalListRef = useListRef(null);
  const handleItemsBuilt = React.useCallback((optionIndexMap) => {
    optionIndexMapRef.current = optionIndexMap;
  }, []);
  // Handle keyboard navigation by scrolling to highlighted option
  const handleHighlightChange = (event, option) => {
    if (option && internalListRef.current) {
      const index = optionIndexMapRef.current.get(option.displayName);
      if (index !== undefined) {
        internalListRef.current.scrollToRow({ index, align: "auto" });
      }
    }
  };

  return {
    internalListRef,
    optionIndexMapRef,
    handleItemsBuilt,
    handleHighlightChange,
  };
}
